<template>
  <b-container fluid class="home-padding">
    <HeroBanner :experience-type="getMerch">
      <MerchBannerContent />
    </HeroBanner>

    <Sector v-if="listExist('home_templates', getMerch)">
      <Subtitle>
        <template #text>{{ $t('home.merch.sector.title') }}</template>
        <template #side>
          <Button
            shape="outline"
            size="small"
            color="white"
            text="gray"
            hover="exp"
            :to="$trailingSlash(localePath('merch-featured-designs'))"
            >{{ $t('home.merch.sector.button') }}</Button
          >
        </template>
      </Subtitle>
      <Grid :items="list('home_templates', getMerch)" @item-click="vectorClick">
        <template #default="slotProps">
          <NormalVectorOverlay :data="slotProps.data" />
        </template>
      </Grid>
      <div class="d-flex justify-content-center mt-3">
        <Button shape="full" :to="$trailingSlash(localePath('merch-featured-designs'))">{{
          $t('home.merch.sector.button2')
        }}</Button>
      </div>
    </Sector>
    <Sector class="my-5 py-5">
      <b-row align-h="center">
        <b-col md="10" lg="8" xl="6">
          <FindBestDesignsBlocks :links="findLinks" />
        </b-col>
      </b-row>
    </Sector>
    <Sector v-if="listExist('home_browse_niches', getMerch)">
      <Subtitle>
        <template #text>{{ $t('home.merch.sector.title2') }}</template>
        <template #side>
          <Button
            shape="outline"
            size="small"
            font-size="13"
            color="white"
            text="gray"
            hover="exp"
            :to="$urlBuilder('niches-home')"
            >{{ $t('home.merch.sector.button') }}</Button
          >
        </template>
      </Subtitle>
      <Carousel
        :items="list('home_browse_niches', getMerch)"
        :height="carouselHeight"
        :margin-right="15"
        :margin-bottom="10"
        :see-more-enabled="true"
        :border-radius="10"
        :see-more-link="$urlBuilder('niches-home')"
        :show-shadow="true"
        :show-bottom-bar="true"
        :show-bottom-see-more="false"
        :is-grouper="true"
      >
        <template #bottomBar="slotProps">
          <CollectionsBottomBar :show-see-more="false" :data="slotProps.data" />
        </template>
      </Carousel>
    </Sector>
    <Sector v-show="!isUserSubscriptor" class="mb-5">
      <InfoSection
        :title="$t(firstInfoSection.title)"
        :background-image="firstInfoSection.backgroundImage"
        :font-color="firstInfoSection.fontColor"
        :description="$t(firstInfoSection.description)"
      >
        <template #action-btn>
          <Button
            shape="full"
            color="white"
            text="exp"
            :to="$urlBuilder('plans', { keyword: 'home' })"
            >{{ $t('home.merch.sector3.button') }}</Button
          >
        </template>
      </InfoSection>
    </Sector>
    <Sector v-if="listExist('prehome_tshirt_psd', getMerch)" class="mb-5">
      <Subtitle>
        <template #text>{{ $t('home.pre-home.scalablePsd.title') }}</template>
        <template #side>
          <Button
            shape="outline"
            size="small"
            font-size="13"
            color="white"
            text="gray"
            hover="exp"
            :to="
              $urlBuilder('search', {
                path: 'merch-psd',
                keyword: 't-shirt-template'
              })
            "
          >
            {{ $t('home.merch.sector6.button') }}
          </Button>
        </template>
      </Subtitle>
      <Carousel
        :items="list('prehome_tshirt_psd', getMerch)"
        :height="carouselHeight"
        :see-more-enabled="true"
        :see-more-link="
          $urlBuilder('search', {
            path: 'merch-psd',
            keyword: 't-shirt-template'
          })
        "
        class="mt-2"
        @vector-click="vectorClick"
      >
        <template #overlay="slotProps">
          <NormalVectorOverlay :data="slotProps.data" />
        </template>
      </Carousel>
    </Sector>
    <Sector class="my-5">
      <TShirtMakerBanner />
    </Sector>
    <Sector v-if="listExist('home_pngs', getMerch)" class="mt-5">
      <Subtitle>
        <template #text>{{ $t('home.merch.sector6.title') }}</template>
        <template #side
          ><EditableColorsIcons link="merch-png" :see-more-text="$t('home.merch.sector6.button')"
        /></template>
      </Subtitle>
      <Carousel
        :items="list('home_pngs', getMerch)"
        :height="170"
        :margin-right="15"
        :margin-bottom="45"
        :see-more-enabled="true"
        :border-radius="10"
        :see-more-link="$trailingSlash(localePath('merch-png'))"
        @vector-click="vectorClick"
      >
        <template #overlay="slotProps">
          <NormalVectorOverlay :data="slotProps.data" />
        </template>
      </Carousel>
    </Sector>
    <Sector>
      <MockupSection
        :title="$t('home.merch.sector7.title')"
        color-img="https://cdn.vexels.com/img/color-guy.jpg"
        :color-img-alt="$t('home.pre-home.mockupSection.colouredMockupAlt')"
        white-img="https://cdn.vexels.com/img/white-guy.jpg"
        :white-img-alt="$t('home.pre-home.mockupSection.whiteMockupAlt')"
        :reserved-width="927"
        :reserved-height="606"
      >
        <template #action-btn>
          <div class="button-mockup col-12 col-sm-6 justify-content-end">
            <Button
              class="mr-3"
              shape="full"
              :to="
                $urlBuilder('search', {
                  path: 'merch-psd',
                  keyword: 'mockups'
                })
              "
            >
              {{ $t('home.merch.sector7.button') }}
            </Button>
          </div>
          <div class="button-mockup col-12 col-sm-6 justify-content-start">
            <Button class="ml-3" shape="full" to="https://mockups.vexels.com/" :blank="true">
              {{ $t('home.merch.sector7.button2') }}
            </Button>
          </div>
        </template>
      </MockupSection>
    </Sector>
    <Sector class="mb-5">
      <CantFind
        :title="$t('home.merch.cantFind.title')"
        :description="$t('home.merch.cantFind.description')"
        :alt-image="$t('home.merch.cantFind.altImage')"
        :request-text="
          $auth.loggedIn ? $t('home.merch.cantFind.request') : $t('home.merch.cantFind.learnMore')
        "
      />
    </Sector>
  </b-container>
</template>

<script>
import Button from 'UI/Button';
import InfoSection from '@/components/Home/Merch/InfoSection';
import Sector from '@/components/Home/Sector';
import CantFind from '@/components/Home/CantFind';
import MockupSection from '@/components/Home/MockupSection';
import Grid from '@/components/Common/Grid';
import Subtitle from '@/components/Home/Subtitle';
import Carousel from '@/components/Common/Carousel';
import CollectionsBottomBar from '@/components/Collections/CollectionsBottomBar';
import NormalVectorOverlay from '@/components/Vectors/NormalVectorOverlay';
import EditableColorsIcons from '@/components/Home/Design/EditableColorsIcons';
import FindBestDesignsBlocks from '@/components/Common/FindBestDesignsBlocks';
import TShirtMakerBanner from '@/components/Home/Merch/TShirtMakerBanner';
import HeroBanner from '@/components/Home/HeroBanner';
import MerchBannerContent from '@/components/Home/Merch/MerchBannerContent';
import { mapGetters } from 'vuex';

export default {
  name: 'Merch',
  components: {
    InfoSection,
    CantFind,
    HeroBanner,
    MerchBannerContent,
    Button,
    Sector,
    Subtitle,
    Grid,
    Carousel,
    CollectionsBottomBar,
    NormalVectorOverlay,
    EditableColorsIcons,
    FindBestDesignsBlocks,
    MockupSection,
    TShirtMakerBanner
  },
  data() {
    return {
      carouselHeight: 180,
      findLinks: ['merch-trending-designs', 'merch-latest-designs', 'categories']
    };
  },
  async fetch() {
    const merch = this.$store.getters['experience/getMerch'];

    await Promise.all([
      this.$store.dispatch('listings/GET_LIST', {
        name: 'home_templates',
        type: 'preview',
        exp: merch
      }),
      this.$store.dispatch('listings/GET_LIST', {
        name: 'home_browse_niches',
        type: 'niches',
        exp: merch
      }),
      this.$store.dispatch('listings/GET_LIST', {
        name: 'home_pngs',
        type: 'preview',
        exp: merch
      }),
      this.$store.dispatch('listings/GET_LIST', {
        name: 'prehome_tshirt_psd',
        type: 'preview',
        exp: merch
      })
    ]);
  },
  computed: {
    ...mapGetters('listings', ['list', 'listExist']),
    ...mapGetters('experience', ['getMerch']),
    experienceColor() {
      return this.$store.getters['experience/getExperienceColor'];
    },
    isUserSubscriptor() {
      return this.$auth.user?.isSubscriptor;
    },
    firstInfoSection() {
      return this.$g('HOME_MERCH_FIRST_INFO_SECTION');
    }
  },
  methods: {
    async vectorClick({ item, shownListing }, e) {
      e.preventDefault();
      this.$bvModal.show('preview-vector-modal');
      await this.$store.dispatch('vectors/OPEN_PREVIEW', { item, list: shownListing });
    }
  }
};
</script>

<style lang="scss" scoped>
.card {
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.10196);
  border: none;
  border-radius: 10px;
}
.card p {
  font-size: 14px;
  color: #6b6b6b;
}
.large-btn {
  width: 250px;
  font-weight: 500;
  font-size: 18px;
  @include sm {
    width: max-content;
    max-width: 100%;
  }
}
.red-text {
  color: #ff5a70;
}
</style>
